import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Icon, Row, Col } from 'antd';
import BasePage from 'components/BasePage';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormSelect from 'components-desktop/FormSelect';
import FormSelect2 from 'components-desktop/FormSelect2';
import FormDatePicker from 'components-desktop/FormDatePicker';
import Panel from 'components-desktop/Panel';
import helpers from 'misc/helpers';
import TablePayrolls from './components/TablePayrolls';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class CaregiverPayrollsPage extends BasePage {
  constructor(props) {
    super(props);

    this.state = {
      caregiverNames: [],
      coordinators: [],
      teams: [],
      timeFormat: 'Standard',

      tableKey: null,
      payrollReport: null,
      isGenerateButton1Enable: true,
      isGenerateButton2Enable: true,
    };
  }

  componentDidMount() {
    this.getSearchCriteria();
  }

  getTitle() {
    return 'Caregiver Payrolls';
  }

  async getSearchCriteria() {
    const promise1 = this.callServiceForObjects('/caregiver/get_id_names');
    const promise2 = this.callServiceForObjects('/coordinator/get_id_names');
    const promise3 = this.callServiceForObjects('/team/get_id_names');

    const results = await Promise.all([promise1, promise2, promise3]);

    this.setState({
      caregiverNames: results[0],
      coordinators: results[1],
      teams: results[2],
    });
  }

  async generateReport(values, reportType) {
    try {
      this.setState({
        [reportType === 'weekly' ? 'isGenerateButton1Enable' : 'isGenerateButton2Enable']: false,
      });

      const methodName = reportType === 'weekly' ? 'generate_weekly' : 'generate_biweekly';
      const params = {
        caregiverId: helpers.extraFormSelectValue(values, 'caregiverId'),
        coordinatorId: helpers.extraFormSelectValue(values, 'coordinatorId'),
        teamId: helpers.extraFormSelectValue(values, 'teamId'),
        backpay: helpers.extraFormSelectValue(values, 'backpay'),
        weekEndDate: helpers.extraFormDateValue(values, 'weekEndDate'),
      };
      const payrollReport =
          await this.callServiceForRemoteObject(`/report/caregiver_payroll/${methodName}`, params);

      this.setState({
        timeFormat: helpers.extraFormSelectValue(values, 'timeFormat'),
        tableKey: `${Date.now()}_${Math.random()}`,
        payrollReport,
      });
    } finally {
      this.setState({
        [reportType === 'weekly' ? 'isGenerateButton1Enable' : 'isGenerateButton2Enable']: true,
      });
    }
  }

  onGenerateClick(reportType) {
    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.generateReport(values, reportType);
      }
    });
  }

  renderContent() {
    const { form } = this.props;
    const {
      caregiverNames,
      coordinators,
      teams,
      timeFormat,
      tableKey,
      payrollReport,
      isGenerateButton1Enable,
      isGenerateButton2Enable,
    } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          color="blue_hoki"
          title="Generate Caregiver Payrolls Report by Criteria"
          icon="file-pdf"
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect2
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="caregiverId"
                    label="Caregiver Name"
                    placeholder="Select..."
                    options={caregiverNames}
                    showIdAfterName
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="coordinatorId"
                    label="Coordinator"
                    placeholder="Select..."
                    options={coordinators}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="teamId"
                    label="Team"
                    placeholder="Select..."
                    options={teams}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="backpay"
                    label="Backpay"
                    placeholder="Select... (+0 days to +3 days)"
                    options={[
                      { value: 'backpay1', text: 'Backpay 1 (+4 days to +10 days)' },
                      { value: 'backpay2', text: 'Backpay 2 (+11 days)' },
                    ]}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="timeFormat"
                    label="Time Format to Display"
                    placeholder="Select..."
                    allowClear={false}
                    initialValue="Standard"
                    options={['Standard', 'Decimal']}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="weekEndDate"
                    label="Week End Date"
                    placeholder="mm/dd/yyyy"
                    isRequired
                    extra="Please select a saturday"
                  />
                </Col>
              </Row>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              <CustomButton
                width="wrap_content"
                height="32px"
                margin="0 32px 0 0"
                backgroundColor="blue_hoki"
                fontSize="14px"
                type="primary"
                disabled={!isGenerateButton2Enable}
                loading={!isGenerateButton1Enable}
                onClick={() => this.onGenerateClick('weekly')}
              >
                GENERATE WEEKLY
                <Icon type="arrow-right" />
              </CustomButton>

              <CustomButton
                width="wrap_content"
                height="32px"
                backgroundColor="blue_hoki"
                fontSize="14px"
                type="primary"
                disabled={!isGenerateButton1Enable}
                loading={!isGenerateButton2Enable}
                onClick={() => this.onGenerateClick('biweekly')}
              >
                GENERATE BIWEEKLY
                <Icon type="arrow-right" />
              </CustomButton>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>

        {payrollReport &&
          <TablePayrolls
            key={tableKey}
            width="match_parent"
            height="wrap_content"
            margin="0 0 24px 0"
            title={`Caregiver Payrolls - Payroll Week (${payrollReport.getString('startDateUsString')} - ${payrollReport.getString('endDateUsString')})`}
            timeFormat={timeFormat}
            payrollReport={payrollReport}
          />
        }
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
CaregiverPayrollsPage.propTypes = propTypes;

export default Form.create()(CaregiverPayrollsPage);
